import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminFranchisesQueryKey,
  useDeleteSuperAdminFranchisesFranchiseId,
  useGetSuperAdminFranchises,
} from '@agos/api-client/platform'
import { T, tt } from '@agos/shared'
import { EmptyState, PageHeader, Spinner, StatusBadge, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Globe, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  countsNote,
  franchisesOf,
  noDomainNote,
  primaryDomainOf,
  purgeConfirmation,
  purgeIsArmed,
  removeDescription,
  removeTitle,
  secretsNote,
  type Franchise,
  type RemoveKind,
} from './franchises'

/**
 * `/franchises` — the Control Tower's franchise list.
 *
 * Phase M's last page, unblocked by the owner's choice of a seeded development super-admin. Everything
 * on it is measured against the running server rather than read: the envelope shape, the 32 fields, the
 * masked `databaseUrl` and the presence-only key flags. See `franchises.ts`.
 */
export function FranchisesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetSuperAdminFranchises()
  const franchises = useMemo(() => franchisesOf(data), [data])

  const [removing, setRemoving] = useState<{ franchise: Franchise; kind: RemoveKind } | null>(null)
  const [typed, setTyped] = useState('')

  const remove = useDeleteSuperAdminFranchisesFranchiseId({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSuperAdminFranchisesQueryKey() })
        setRemoving(null)
        setTyped('')
        toast({ title: tt('Hotovo') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Nepodarilo sa'),
          description: apiErrorMessage(caught, ''),
        }),
    },
  })

  const armed =
    removing === null ||
    removing.kind === 'deactivate' ||
    purgeIsArmed(removing.franchise, typed)

  return (
    <div className="space-y-5" data-franchises>
      <PageHeader
        title={tt('Pobočky')}
        description={countsNote(franchises)}
        icon={<Building2 className="w-5 h-5" />}
      />

      <p className="text-[11px] text-muted-foreground" data-secrets-note>
        {secretsNote()}
      </p>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : franchises.length === 0 ? (
        <EmptyState
          icon={<Building2 className="w-10 h-10" />}
          title={tt('Žiadne pobočky')}
          description={tt('Centrálny register nevrátil ani jednu.')}
        />
      ) : (
        <ul className="space-y-2">
          {franchises.map((franchise) => (
            <li
              key={franchise.id}
              data-franchise={franchise.id}
              className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-3"
            >
              <div className="flex-1 min-w-[14rem]">
                <p className="text-sm font-bold">
                  {franchise.name}
                  {!franchise.isActive && (
                    <span className="ml-2">
                      <StatusBadge color="zinc" label={tt('neaktívna')} />
                    </span>
                  )}
                </p>
                {franchise.domains.length === 0 ? (
                  <p className="text-[11px] text-amber-500" data-no-domain={franchise.id}>
                    {noDomainNote()}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Globe className="w-3 h-3 shrink-0" />
                    {primaryDomainOf(franchise)}
                    {franchise.domains.length > 1 && (
                      <span className="text-[11px]">
                        {' '}
                        +{franchise.domains.length - 1}
                      </span>
                    )}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground" data-franchise-meta={franchise.id}>
                  {franchise.slug}
                  {franchise.packageName !== null && ` · ${franchise.packageName}`}
                  {franchise.hasMasterApiKey && ` · ${tt('má API kľúč')}`}
                  {franchise.hasWireguardConfig && ` · ${tt('má WireGuard')}`}
                </p>
              </div>

              {/* FB-P01: two different actions, two different buttons. Legacy had one, behind a boolean. */}
              <button
                type="button"
                data-deactivate={franchise.id}
                disabled={!franchise.isActive}
                onClick={() => {
                  setTyped('')
                  setRemoving({ franchise, kind: 'deactivate' })
                }}
                className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold disabled:opacity-50"
              >
                <T>Deaktivovať</T>
              </button>
              <button
                type="button"
                data-purge={franchise.id}
                onClick={() => {
                  setTyped('')
                  setRemoving({ franchise, kind: 'purge' })
                }}
                aria-label={`${tt('Vymazať z registra')} ${franchise.name}`}
                className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-xl border border-destructive/40 text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {removing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-remove-dialog>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setRemoving(null)}
          />
          <section className="relative w-full max-w-md rounded-2xl bg-background border border-border p-5 space-y-3">
            <h2 className="text-base font-bold" data-remove-title>
              {removeTitle(removing.kind)}
            </h2>
            <p className="text-xs text-muted-foreground" data-remove-description>
              {removeDescription(removing.kind, removing.franchise)}
            </p>

            {removing.kind === 'purge' && (
              <label className="block space-y-1">
                <span className="text-[11px] text-muted-foreground">
                  <T>Potvrďte napísaním</T> <strong>{purgeConfirmation(removing.franchise)}</strong>
                </span>
                <input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  aria-label={tt('Potvrdenie')}
                  data-purge-confirm
                  className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
                />
              </label>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                data-remove-cancel
                onClick={() => setRemoving(null)}
                className="min-h-[48px] px-4 rounded-xl border border-border text-sm font-semibold"
              >
                <T>Zrušiť</T>
              </button>
            <button
              type="button"
              data-remove-confirm
              disabled={!armed || remove.isPending}
              onClick={() =>
                remove.mutate({
                  franchiseId: removing.franchise.id,
                  ...(removing.kind === 'purge' ? { params: { force: true } } : {}),
                })
              }
              className={cn(
                'flex-1 min-h-[48px] rounded-xl font-bold disabled:opacity-50',
                removing.kind === 'purge'
                  ? 'bg-destructive text-white'
                  : 'bg-primary text-primary-foreground',
              )}
            >
              {remove.isPending ? <Spinner /> : removeTitle(removing.kind)}
            </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
